import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Chip,
  Avatar,
  Alert,
  AlertTitle,
  Skeleton,
  Paper,
  Fab,
  useTheme,
  alpha,
  Stack
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const TrainerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const [trainerProfile, setTrainerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        const profileResponse = await api.get('/trainer-profiles/me');
        setTrainerProfile(profileResponse.data.data);
      } catch (error) {
        console.log('Trainer profile not found');
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'My Profile',
      description: 'Update specializations and availability',
      icon: <PersonIcon />,
      action: () => navigate('/trainer/profile'),
      color: theme.palette.primary.main,
    },
    {
      title: 'Schedule',
      description: 'Manage your availability',
      icon: <ScheduleIcon />,
      action: () => navigate('/trainer/schedule'),
      color: theme.palette.success.main,
    },
    {
      title: 'Students',
      description: 'View class participants',
      icon: <GroupsIcon />,
      action: () => navigate('/trainer/students'),
      color: theme.palette.info.main,
    },
  ];

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          <AlertTitle>Something went wrong</AlertTitle>
          {error}
          <Button onClick={fetchDashboardData} sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 1
          }}
        >
          Hello, Trainer {user?.firstName}! 👋
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {trainerProfile 
            ? `Specializing in: ${trainerProfile.specializations?.join(', ') || 'General Fitness'}`
            : 'Complete your profile to get started'
          }
        </Typography>
      </Box>

      {/* Profile Status Alert */}
      {!trainerProfile && !loading && (
        <Alert
          severity="warning"
          action={
            <Button
              color="warning"
              variant="outlined"
              onClick={() => navigate('/trainer/profile')}
            >
              Complete Profile
            </Button>
          }
          sx={{ mb: 3 }}
        >
          <AlertTitle>Profile Incomplete</AlertTitle>
          Complete your trainer profile to start scheduling classes
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid size={12}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUpIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>Quick Actions</Typography>
                </Box>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                {quickActions.map((action, index) => (
                  <Grid size={3} key={index}>
                    <Paper
                      elevation={0}
                      onClick={action.action}
                      sx={{
                        p: 2.5,
                        height: '140px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: `1px solid ${alpha(action.color, 0.2)}`,
                        background: `linear-gradient(135deg, ${alpha(action.color, 0.05)}, ${alpha(action.color, 0.1)})`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[4],
                          background: `linear-gradient(135deg, ${alpha(action.color, 0.1)}, ${alpha(action.color, 0.2)})`
                        }
                      }}
                    >
                      <Avatar sx={{
                        bgcolor: action.color,
                        mb: 1.5,
                        width: 48,
                        height: 48
                      }}>
                        {action.icon}
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        {action.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {action.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Trainer Profile Summary */}
        <Grid size={12}>
          <Card>
            <CardHeader 
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>Profile Summary</Typography>
                </Box>
              }
              action={
                trainerProfile && (
                  <Button 
                    variant="outlined" 
                    onClick={() => navigate('/trainer/profile')}
                  >
                    Edit Profile
                  </Button>
                )
              }
            />
            <CardContent>
              {loading ? (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Skeleton width="40%" height={32} sx={{ mb: 2 }} />
                    <Box display="flex" gap={1} mb={2}>
                      <Skeleton width={80} height={24} />
                      <Skeleton width={100} height={24} />
                      <Skeleton width={90} height={24} />
                    </Box>
                    <Skeleton width="30%" height={32} sx={{ mb: 2 }} />
                    <Skeleton width="100%" height={20} sx={{ mb: 1 }} />
                    <Skeleton width="80%" height={20} />
                  </Grid>
                </Grid>
              ) : trainerProfile ? (
                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Specializations
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                      {trainerProfile.specializations?.map((spec, index) => (
                        <Chip
                          key={index}
                          label={spec}
                          variant="outlined"
                          color="primary"
                          size="small"
                        />
                      ))}
                    </Box>
                    
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Bio
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {trainerProfile.bio || 'No bio provided'}
                    </Typography>
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Availability
                    </Typography>
                    {trainerProfile.weeklyAvailability?.length > 0 ? (
                      <Stack spacing={1}>
                        {trainerProfile.weeklyAvailability.map((slot, index) => (
                          <Box key={index} display="flex" justifyContent="space-between" py={0.5}>
                            <Typography variant="body2" fontWeight={500}>
                              {slot.day}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {slot.startTime} - {slot.endTime}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No availability set
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Box textAlign="center" py={6}>
                  <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Your trainer profile is not complete
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => navigate('/trainer/profile')}
                    sx={{ mt: 2 }}
                  >
                    Complete Profile
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
        onClick={() => navigate('/trainer/profile')}
      >
        <AddIcon />
      </Fab>
    </Container>
  );
};

export default TrainerDashboard;